import { useEffect, useState } from "react";
import { Button } from "primereact/button";

import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";

import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL } from "../../style";
import WebsiteMediaInput from "./WebsiteMediaInput";
import FieldNote from "./common/FieldNote";

export default function WebsiteHeader() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();
    const [record, setRecord] = useState();

    useEffect(() => {
        requestAPI({
            requestPath: "website/header",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRecord(data);
                else setError("Couldn't load Header");
            },
        });
    }, [requestAPI]);

    const saveRecord = () => {
        requestAPI({
            requestPath: "website/header",
            requestMethod: "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Update Header !", life: 2000 }),
            onResponseReceieved: (updated, responseCode) => {
                if (updated && responseCode === 200) {
                    setRecord(updated);
                    showToast({ severity: "success", summary: "Updated", detail: "Header Updated", life: 1000 });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: updated?.error || "Failed To Update Header !", life: 2000 });
                }
            },
        });
    };

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Header" />
            <TabHeader
                className="px-3 pt-3"
                title="Top bar"
                highlights={["The bar at the top of every page", "Leave a field blank to use the site name, tagline, logo, or phone"]}
            />
            <Divider />
            {loading ? <Loading /> : null}
            {error ? <NoContent error={error} /> : null}
            {!loading && !error && record ? (
                <div className="flex-1 overflow-y-scroll p-3 flex flex-column">
                    <WebsiteMediaInput label="Header logo" field="header_logo" hint="Required size: 256 ? 256 px (square). A larger logo is scaled to fit the small header square. Leave blank to use the site logo." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="header_brand_name" className="w-full" value={record?.header_brand_name ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, header_brand_name: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="header_brand_name">Name in the header</label>
                        </FloatLabel>
                        <FieldNote text="Required. The name next to the logo. Example: SAHAS." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="header_brand_tagline" className="w-full" value={record?.header_brand_tagline ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, header_brand_tagline: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="header_brand_tagline">Small line under the name</label>
                        </FloatLabel>
                        <FieldNote text="Example: Smart Studies ? Commerce. Leave blank to use the site tagline." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="header_phone" className="w-full" value={record?.header_phone ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, header_phone: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="header_phone">Phone in the header</label>
                        </FloatLabel>
                        <FieldNote text="The call button at the right of the top bar. Leave blank to use the site phone." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="header_cta_url" className="w-full" value={record?.header_cta_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, header_cta_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="header_cta_url">App button link</label>
                        </FloatLabel>
                        <FieldNote text="Where the App button goes. Example: /#app." />
                    </div>
                    <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={saveRecord} />
                </div>
            ) : null}
        </div>
    );
}
